import React, { useState, useEffect } from "react";
import { MessageSquare } from "lucide-react";
import { useLanguage } from "components/LanguageProvider.js";
import Accordion from "components/ui/accordion/Accordion";
import Slider from "components/ui/slider/Slider";
import EventSection from "components/ui/sections/EventSection";
import FeedbackSection from "components/ui/sections/FeedbackSection";
import useCurrentUser from "config/useCurrentUser";
import { trackEvent } from "config/firebase";

const content = {
  en: {
    title:
      "National Education Foundation | «Ազգային Կրթություն» կրթական հիմնադրամ",
    nav: {
      home: "Home",
      about: "About Us",
      work: "Our Work",
      events: "Events",
      donate: "Donate",
      contact: "Contact",
      openLetter: "Open Letter",
    },
    home: {
      heading: "Empowering Through Education",
      subheading:
        "Fostering growth in science, culture, and social development for a brighter future.",
      button: "For More Details",
    },
    about: {
      heading: "Our Foundation's Purpose",
      text1:
        'The "National Education" educational foundation is a non-commercial organization without membership, based on the voluntary property contributions of its founder. Our purpose is to pursue educational, scientific, cultural, social, environmental, and other public-benefit goals.',
      text2:
        'Our activities are guided by the Constitution of the RA, the "Law on Foundations" of the RA, the Civil Code, and our charter.',
      governance: "Our Governance",
      trustees: {
        title: "Board of Trustees",
        description:
          "The supreme governing body, responsible for strategic planning and oversight.",
      },
      director: {
        title: "Director",
        description:
          "Manages daily operations and implements the Board's decisions.",
      },
    },
    work: {
      heading: "Our Areas of Impact",
      subheading:
        "We develop and implement programs to advance key sectors in Armenia and the Diaspora, fostering innovation and sustainable growth.",
      areas: [
        "Education",
        "Science",
        "Culture",
        "Social Development",
        "Environment",
        "And more...",
      ],
    },
    donate: {
      heading: "Join Us in Making a Difference",
      subheading:
        "Your generous donation will directly support our programs. Every contribution, big or small, helps us build a brighter future for Armenia and the Diaspora.",
      formTitle: "Make a Donation",
      comingSoon: "Coming Soon",
      amountLabel: "Amount (USD)",
      nameLabel: "Full Name",
      emailLabel: "Email Address",
      button: "Proceed to Payment",
    },
    contact: {
      heading: "Get in Touch",
      subheading: "We welcome your questions and partnership inquiries.",
      email: "azgayinakademia@gmail.com",
      address: "Republic of Armenia, Yerevan, Azatutyan Ave 24/10, 0037",
    },
    FAQ: {
      heading: "Frequently Asked Questions",
      accordions: [
        {
          title: "Question 1. What is the “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> The “National Educational System” is
                designed as a comprehensive alternative to the current general
                education system. It encompasses the goals and content of the
                “National Curriculum,” subject concepts, programs, textbooks,
                and the technological tools that make them accessible to both
                teachers and students.
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 2. What dictates the need to create a “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> It is no longer a secret that current
                authorities are attempting to distort national values through
                education—reshaping history under the guise of critical
                thinking, diminishing our civilizational heritage, and promoting
                a materialistic “proud citizen” devoid of national identity and
                human dignity. We must not only resist this but also create our
                own national educational system. Modern technological tools now
                make this possible. The necessity of creating a “National
                Educational System” is dictated by:
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>
                    The imperative to modernize national civilizational values
                    in the 21st century and transmit them to future generations.
                  </li>
                  <li>
                    The need to align the national general education system with
                    internationally accepted standards while preserving its
                    civilizational foundations.
                  </li>
                  <li>
                    The uncertainty and gaps in education and national
                    upbringing caused by the so-called “reforms” of the Republic
                    of Armenia’s general education system.
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 3. Who assumes responsibility for implementing the “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> Responsibility lies with the “National
                Education Council”, composed of respected professionals in the
                field of education who are conscious of their mission. The
                Council oversees both the content and the implementation of the
                system through the self-organization of specialists. The actual
                development of subject concepts, programs, and teaching
                materials is carried out by “Subject Professional Groups”, which
                also involve students and IT specialists in the creative
                process. This forms a multi-layered environment of
                collaboration, united by the goal of serving the public good.
                Currently, more than 50 individuals are engaged in five pilot
                courses, resulting in the first set of content for the “National
                School.”
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 4. What is the main goal of creating the “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong>
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>
                    To establish the “National School” (currently operating in a
                    virtual format).
                  </li>
                  <li>
                    To harmonize educational and moral functions for shaping a
                    virtuous, free, and just Armenian citizen of the 21st
                    century.
                  </li>
                  <li>
                    To develop the image of the “National Teacher”, who bears
                    the unique responsibility of realizing this mission.
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 5. Who are the beneficiaries of the “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> The system aims to fill educational and
                technological gaps in general education. Its beneficiaries
                include all participants in the educational process:
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>Teachers</li>
                  <li>Students</li>
                  <li>Parents</li>
                  <li>State and local government education bodies</li>
                  <li>
                    General education schools and other institutions engaged in
                    teaching
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 6. What is the formal basis of the “National Educational System”?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> The formal basis is the
                state-registered “National Education” Educational Foundation.
                Its Board of Trustees serves as the “National Education
                Council,” responsible for overseeing the entire process. In the
                future, “Subject Professional Groups” are expected to operate in
                a more regulated framework through educational grant programs.
              </div>
            </div>
          ),
        },
        {
          title:
            "Question 7. What kind of public support does the “National Educational System” expect, and from whom?",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans text-[18px] font-medium leading-normal">
                <strong>Answer:</strong> The system relies on three levels of
                public support:
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>
                    <strong>Professional circles</strong> – educators and IT
                    specialists who oppose the erosion of national identity and
                    are committed to creating public good.
                  </li>
                  <li>
                    <strong>National capital</strong> – business leaders who
                    recognize that without a strong nation and state, their own
                    property is ultimately insecure. Their responsibility is to
                    invest today in the nation’s future. These contributions, in
                    the form of donations to the Foundation, will fund
                    transparent and accountable grant activities.
                  </li>
                  <li>
                    <strong>Society</strong> – broad public support, grounded in
                    awareness of the issue’s importance, will ensure that the
                    creation and implementation of the “National Educational
                    System” becomes unstoppable, reversing the current course of
                    national decline.
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },
    footer: {
      title: "National Education Foundation",
      copyright: "All Rights Reserved.",
      currentYear: new Date().getFullYear(),
    },
    events: {
      heading: "Events",
      list: [
        {
          title: "Workshop: 'What the Armenian Alphabet Should Be Like'",
          description:
            "The National Education Council emphasizes the importance of the Armenian Alphabet as a cornerstone of national identity. During the workshop, the launch of the 'National Teacher' award in its category will also be presented.",
          date: "October 4, 13:00",
          location: "Yerevan, Elite Plaza Business Center",
          contact: "—",
        },
        {
          title:
            "Invitation to a Closed Meeting-Workshop by the 'National Education Council'",
          description:
            "Purpose: to ensure mutual recognition among organizations and individuals concerned with national educational issues, and to create a unified platform for information exchange and cooperation for the establishment of the 'National Education System' and neutralizing challenges threatening national identity. The workshop agenda is open and will focus on ensuring mutual recognition and trust in the activities of educational organizations.",
          date: "September 25, 17:00",
          location:
            "Yerevan, 24/10 Azatutyan Ave., 'Hayots Ashkharhavag' office of the AAH movement",
          contact: "077 090 514",
        },
        {
          title: "Coming Soon: Closed Meeting-Workshop with Historians",
          description:
            "An alternative comprehensive solution is needed to counter the denial of Armenian history in general education. The 'National Education Council' aims to present Armenian history with a unified approach as a complete civilizational mission.",
          date: "Coming Soon",
          location: "—",
          contact: "—",
        },
        {
          title:
            "Commemorative Evening-Workshop: 'The Hero and Heroism in Education'",
          description:
            "Dedicated to Monte's birthday. Details will be announced soon.",
          date: "November 25",
          location: "Details will be announced.",
          contact: "—",
        },
        {
          title: "National Teacher Award Ceremony",
          description:
            "The 'National Education Council' presents the 'National Teacher' award ceremony.",
          button: { title: "Learn More", link: "/award" },
          date: "2026 May 28",
          location: "Details will be announced.",
          contact: "—",
        },
      ],
    },
    openLetter: {
      heading: "Open Letter of the 'National Education Council'",
      subheading:
        "To the political forces participating in the 2026 parliamentary elections‼️",
      description:
        "🚩 The 'National Education Council' considers the upcoming parliamentary elections on June 7, 2026, to be beyond the simple logic of a political struggle, viewing it as a crossroads of values and civilization for the Armenian nation. Therefore, the Council addresses the participating political forces with an open letter to clarify their views on the future of the scientific and educational sector, expecting public answers to three simple questions.\n\nIn the event of holding power, are they ready to:",
      questions: [
        {
          letter: "A",
          text: "Build the content of general education on the basis of a national-civilizational system of values, in contrast to current trends of de-nationalization,",
        },
        {
          letter: "B",
          text: "Guarantee that the currently circulated plan to close 229 schools will be cancelled and the schools will not be closed,",
        },
        {
          letter: "C",
          text: "Develop and implement a national strategy for the progressive development of higher education and science.",
        },
      ],
      outro:
        "We would like to add that all answers (including the absence of an answer) will be published.",
      signature: "With respect, 'National Education Council'",
      responsesTitle: "Official Responses from Political Forces",
      responses: [
        {
          orgName: "National Democratic Pole (NDP)",
          title: "Official Response of the 'National Democratic Pole' (NDP)",
          date: "May 27, 2026",
          status: "Accepted (Yes to all questions)",
          intro:
            "Dear National Education Council,\n\nYour open letter dated May 27, 2026, has been received. The Council of the National Democratic Pole (NDP) has carefully read the three questions and presents its official position below. Our answer to all three questions is: Yes.",
          answers: [
            {
              question:
                "A. Build the content of general education on the basis of a national-civilizational system of values, in contrast to current trends of de-nationalization?",
              reply: "Yes, without reservation.",
              details:
                "The NDP believes that the Armenian school is called to form a national-civilizational character. Armenian education must be rooted in Armenian Apostolic traditions, Armenian national history, and Armenian cultural identity. Armenian children should learn in school that Artsakh is their homeland, that the global recognition of the Armenian Genocide is a documented, established historical fact, and that the Mother See of Holy Etchmiadzin is an inseparable carrying pillar of Armenian identity and cannot be replaced by any political ideology. The formation of national-civic values means that the Armenian child graduates as a full citizen, oriented towards the homeland and sovereign self-determination. Any curriculum that erases Artsakh, margins the Genocide, or makes the Mother See just another institution among equals is not a national curriculum—it is a de-nationalization program. The NDP will not allow this.",
            },
            {
              question:
                "B. Guarantee that the currently circulated plan to close 229 schools will be cancelled and the schools will not be closed?",
              reply: "Yes, the program to close 229 schools must be cancelled.",
              details:
                "Schools in rural and regional communities are not optimization expenses. They are a prerequisite for the survival of those communities. The closure of a rural school accelerates depopulation, and depopulation cannot be defended or managed. The NDP views school infrastructures as strategic national infrastructures, on the same level as roads, border points, and water systems. We will cancel the plan to close 229 schools and ensure that the maintenance of rural schools is funded as a national security obligation, not a social security discretion.",
            },
            {
              question:
                "C. Develop and implement a national strategy for the progressive development of higher education and science?",
              reply: "Yes, the sovereignty of Armenia demands this.",
              details:
                "1. A country that cannot educate its own engineers and scientists becomes dependent on others for the knowledge governing its own territory. Armenia's sovereign state capabilities are based on the quality of its own scientific, technical, and intellectual institutions. The NDP undertakes to develop and implement a national strategy for higher education and science, which views Armenian universities and research institutions as pillars of sovereign state capabilities, builds funding mechanisms independent of single donor dependency, and directly links higher education policy with Armenia's national security and economic self-sufficiency. This strategy must place Artificial Intelligence education at its center: a nation that does not train its own AI specialists and does not manage AI development on its own terms surrenders the deciding criterion of sovereignty to others. Armenia must develop these capabilities within the country, rather than importing them on others' terms.",
            },
          ],
          outro:
            "The NDP Council welcomes the initiative of the National Education Council to publicly address this question to all political forces participating in the elections. The three questions raised by you are not administrative questions. They are questions about whether the next parliament understands that the nation's school is the basis of its continuous existence as a nation.\n\nFaith, Homeland, Sovereignty — Armenia is not subject to bargaining.",
          signature:
            "With respect,\nCouncil of the National Democratic Pole (NDP)\nYerevan, May 27, 2026",
        },
        {
          orgName: "'STRONG ARMENIA' Alliance",
          title:
            "Response of the 'STRONG ARMENIA' Alliance to the Open Letter of the 'National Education Council'",
          date: "May 28, 2026",
          status: "Accepted (Yes to all questions)",
          intro:
            "Dear Members of the 'National Education Council',\n\nThank you for initiating a crucial public discussion on the future of the education and science sectors. The 'STRONG ARMENIA' Alliance fully shares the conviction that the development of the Republic of Armenia is impossible without a competitive and future-oriented educational and scientific system based on national values. We answer your raised questions as follows:",
          answers: [
            {
              question:
                "A. Regarding the national-civilizational foundations of the content of general education:",
              reply: "Yes!",
              details:
                "The 'STRONG ARMENIA' Alliance is convinced that the content of general education must be formed on the basis of the Armenian people's national identity, historical memory, cultural heritage, statehood, and awareness of their civilizational mission. We emphasize the integration of universal values and modern knowledge with the national system of values, excluding any manifestation of educational de-nationalization. The Armenian school must educate conscious, patriotic, responsible, and developed citizens.",
            },
            {
              question: "B. Regarding the school closure program:",
              reply: "Yes!",
              details:
                "The Alliance opposes the mechanical school closure policy, especially in border, rural, and small communities where the school is not only an educational institution but also a crucial component of community life, culture, and national security. In the event of forming a government, we will review the currently circulated plan to close 229 schools and ensure that every decision is made solely on the principles of improving the quality of education, the interests of children, and maintaining the viability of communities, taking into account the opinions of the residents of the given locality.",
            },
            {
              question:
                "C. Regarding the national strategy for higher education and science:",
              reply: "Yes!",
              details:
                "The 'STRONG ARMENIA' Alliance considers the radical development of higher education and science sectors as a priority. We will initiate the development and implementation of a long-term national strategy for education, science, innovation, and technological development, aimed at increasing the competitiveness of Armenian universities, preserving and expanding scientific potential, encouraging young scientists, as well as establishing an effective connection between science and the economy. We are convinced that building a knowledge-based economy and state is one of the most important prerequisites for Armenia's security, development, and international competitiveness.",
            },
          ],
          outro:
            "We are confident that the formation of a national consensus around education and science is one of the most important guarantees for the strengthening of our statehood.",
          signature: "With respect,\n'STRONG ARMENIA' Alliance",
        },
        {
          orgName: "'Armenian National Congress' (ANC) Party",
          title:
            "Response of the 'Armenian National Congress' (ANC) Party to the Open Letter of the 'National Education Council'",
          date: "May 29, 2026",
          status: "Accepted (Yes / Aligned)",
          intro:
            "In response to your open letter addressed to the political forces participating in the parliamentary elections of June 7, 2026, we present the position of the Armenian National Congress.",
          answers: [
            {
              question:
                "A. Regarding the national-civilizational foundations of the content of general education:",
              reply: "Yes!",
              details:
                "Build the content of general education on the basis of national and universal systems of values, in contrast to the current trends of trampling on national values.",
            },
            {
              question: "B. Regarding the school closure program:",
              reply: "Yes (Aligned)!",
              details:
                "Your views formulated in points B and C are fully aligned with the programmatic approaches of the ANC, which you can learn about in detail in the 'Education' section of the pre-election program posted on the party's website anc.am.",
            },
            {
              question:
                "C. Regarding the national strategy for higher education and science:",
              reply: "Yes (Aligned)!",
              details:
                "Your views formulated in points B and C are fully aligned with the programmatic approaches of the ANC, which you can learn about in detail in the 'Education' section of the pre-election program posted on the party's website anc.am.",
            },
          ],
          outro: "Thank you for your approaches and concerns.",
          signature: "With respect,\nArmenian National Congress",
        },
      ],
    },
  },
  hy: {
    title:
      "National Education Foundation | «Ազգային Կրթություն» կրթական հիմնադրամ",
    nav: {
      home: "Գլխավոր",
      about: "Մեր մասին",
      work: "Գործունեություն",
      events: "Իրադարձություններ",
      donate: "Նվիրաբերել",
      contact: "Կապ",
      openLetter: "Բաց նամակ",
    },
    home: {
      heading:
        "«Ազգային կրթության խորհրդի» բաց նամակը՝ 2026-ի խորհրդարանական ընտրություններին մասնակցող քաղաքական ուժերին",
      subheading: `«Ազգային կրթության խորհուրդը» 2026-ի հունիսին 7-ին կայանալիք խորհրդարանական ընտրությունները դուրս է համարում քաղաքական մրցապայքարի պարզ տարմաբանությունից, այն դիտարկելով հայ ազգի համար արժեհամակարգային և քաղաքակրթական ընտրության ճամփաբաժան։ Հետևաբար, խորհուրդը բաց նամակով դիմում է մասնակից քաղաքական ուժերին՝ հստակեցնելու նրանց տեսակետը գիտա-կրթական ոլորտի ճակատագրի վերաբերյալ\` ակնկալելով երեք պարզ հարցի հրապարակային պատասխան․ Իշխանություն ունենալու պարագայում արդյո՞ք պատրաստ են՝ Ա․ Հանրակրթության բովանդակությունը կառուցել ազգային-քաղաքակրթական արժեհամակարգի հենքով\` ի տարբերություն ապազգայնացման ներկա միտումների,
Բ․ Երաշխավորել, որ ներկայում շրջանառության մեջ դրված 229 դպրոցների փակման ծրագիրը կչեղարկվի և դպրոցները չեն փակվի։
Գ․ Մշակել և իրականացնել բարձրագույն կրթության և գիտության առաջընթաց զարգացման ազգային ռազմավարություն։
Հավելենք, որ բոլոր պատասխանները (ներառյալ պատասխանի բացակայությունը) հրապարակվելու են։
Հարգանքներով՝ «Ազգային կրության խորհուրդ»`,
      button: { text: "ԲԱՑ ՆԱՄԱԿ", link: "/CouncilAnnouncement" },
      secondaryButton1: {
        text: "«Հայ Ազգային Կոնգրես» կուսակցության պատասխանը",
        link: "/public-report",
      },
      secondaryButton2: {
        text: "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինքի պատասխանը",
        link: "/public-report",
      },
      secondaryButton3: {
        text: "Ազգային Ժողովրդավարական Բևեռի (ԱԺԲ) պատասխանը",
        link: "/public-report",
      },
    },
    about: {
      heading: "Մեր Հիմնադրամի Նպատակը",
      text1:
        "«Ազգային Կրթություն» կրթական հիմնադրամը հիմնադրի կամավոր գույքային վճարների վրա հիմնված և անդամություն չունեցող ոչ առևտրային կազմակերպություն է, որը հետապնդում է կրթական, գիտական, մշակութային, սոցիալական, բնապահպանական և այլ հանրօգուտ նպատակներ։",
      text2:
        "Մեր գործունեությունը ղեկավարվում է ՀՀ Սահմանադրությամբ, «Հիմնադրամների մասին» ՀՀ օրենքով, ՀՀ Քաղաքացիական օրենսգրքով և մեր կանոնադրությամբ։",
      governance: "Մեր Կառավարումը",
      trustees: {
        title: "Հոգաբարձուների խորհուրդ",
        description:
          "Բարձրագույն կառավարման մարմին, որը պատասխանատու է ռազմավարական պլանավորման և վերահսկողության համար։",
      },
      director: {
        title: "Տնօրեն",
        description:
          "Կառավարում է ամենօրյա գործունեությունը և իրականացնում Խորհրդի որոշումները։",
      },
    },
    work: {
      heading: "Մեր Ազդեցության Ոլորտները",
      subheading:
        "Մենք մշակում և իրականացնում ենք ծրագրեր՝ Հայաստանում և Սփյուռքում առանցքային ոլորտները զարգացնելու համար՝ խթանելով նորարարությունը և կայուն աճը։",
      areas: [
        "Կրթություն",
        "Գիտություն",
        "Մշակույթ",
        "Սոցիալական Զարգացում",
        "Բնապահպանություն",
        "Եվ այլն...",
      ],
    },
    donate: {
      heading: "Միացե՛ք մեզ՝ փոփոխություն կատարելու համար",
      subheading:
        "Ձեր առատաձեռն նվիրատվությունը ուղղակիորեն կաջակցի մեր ծրագրերին։ Յուրաքանչյուր ներդրում օգնում է մեզ կառուցել ավելի պայծառ ապագա Հայաստանի և Սփյուռքի համար։",
      formTitle: "Կատարել Նվիրատվություն",
      comingSoon: "Շուտով",
      amountLabel: "Գումար (ԱՄՆ դոլար)",
      nameLabel: "Անուն Ազգանուն",
      emailLabel: "Էլ. հասցե",
      button: "Անցնել Վճարման",
    },
    contact: {
      heading: "Կապվեք մեզ հետ",
      subheading:
        "Մենք ողջունում ենք ձեր հարցերն ու համագործակցության առաջարկները։",
      email: "azgayinakademia@gmail.com",
      address: "Հայաստանի Հանրապետություն, Երևան, Ազատության պող. 24/10, 0037",
    },
    FAQ: {
      heading: "Հարց ու Պատասխան",
      accordions: [
        {
          title: "Հարց 1․ Ի՞նչ է «Ազգային կրթական համակարգը»․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> «Ազգային կրթական համակարգը» ներկա
                գործող կրթական համակարգի, ավելի հստակ՝ հանրակրթության,
                ամբողջական այընտրանքն է լինելու։ Այն ամբողջացնելու է «Ազգային
                կրթակարգի» նպատակն ու բովանդակությունը, առարկայական
                հայեցակարգերը, ծրագրերն ու դասագրքերը, ինչպես նաև դրանք
                ուսուցիչների ու աշակերտների համար հասանելի դարձնող տեխնոլոգիական
                գործիքները։
              </div>
            </div>
          ),
        },
        {
          title:
            "Հարց 2․ Ինչո՞վ է թելադրված «Ազգային կրթական համակարգի» ստեղծման անհրաժեշտությունը․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> Արդեն ոչ մեկի համար գաղտնիք չէ, որ
                ներկա իշխանությունները նաև կրթության միջոցով են փորձում
                նենգափոխել ազգային ինքնության արժեքները, քննադատական մոտեցման
                քողի տակ ամբողջապես խեղաթյուրել մեր պատմությունը, նսեմացնել
                քաղաքակրթական ժառանգությունն ու առաքելությունը։ Եվ այդպես
                դաստիարակել սերունդ, որի նպատակային օրինակը ազգային
                ինքնությունից ու մարդկային արժանապատվությունից զուրկ, նյութապաշտ
                «հպարտ քաղաքացին» է։ Սրա դեմ ոչ միայն պետք է պայքարել, այլև պետք
                է ստեղծել մեր ազգային կրթական համակարգը, առավել ևս, որ դրա
                հնարավորությունը ժամանակակից տեխնոլոգիական գործիքները տալիս են։
                Ըստ այդմ, կարող ենք սահմանել, որ «Ազգային կրթական համակարգի»
                ստեղծման անհրաժեշտությունը թելադրված է․
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>
                    21-րդ դարում ազգային քաղաքակրթական արժեքների արդիականացման և
                    սերունդներին փոխանցման հրամայականով,
                  </li>
                  <li>
                    Ազգային հանրակրթական համակարգը քաղաքակրթական հիմքի վրա
                    միջազգայնորեն ընդունված չափանիշներին համապատասխանեցնելու
                    հրամայականով,
                  </li>
                  <li>
                    ՀՀ հանրակրթական համակարգում իրականացվող «բարեփոխումների»
                    հետևանքով ստեղծված կրթական և ազգային դաստիարակության
                    անորոշության պարտադրանքով։
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
        {
          title:
            "Հարց 3․ Ովքե՞ր են «Ազգային կրթական համակարգի» պատասխանատվություն ստանձնողները և իրականացողները․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> «Ազգային կրթական համակարգի»
                պատասխանատվություն ստանձնողը «Ազգային կրթության խորհուրդ»-ն է,
                որը կազմված է կրթության ոլորտի վաստակաշատ, սեփական
                պատասախանատվությունը գիտակցող անհատներից։ Այսպիսով, «Ազգային
                կրթութան խորհուրդը»՝ որպես սուբյեկտ, պատասխանատու է և՛ «Ազգային
                կրթակարգի» ամբողջ բովանդակության, և՛ մասնագետների
                ինքնակազմակերպման միջոցով դրա իրացման համար։ Իսկ բուն
                գործընթացը՝ առարկայական հայեցակարգերի, ծրագրերի ու դասանյութերի
                պատրաստումն, իրականացվում է «Առարկայական մասնագիտական խմբերի»
                կողմից, որոնք, ներգրավելով նաև ուսանողներին և ՏՏ ոլորտի
                մասնագետներին, ստեղծում են «Ազգային դպրոցի» ամբողջ
                բովանդակությունը։ Այսինքն, ձևավորված է ստեղծարար
                համագործակցության մի բազմաշերտ միջավայր, որտեղ բոլորի նպատակը
                հանրային բարիքի արարումն է։ Այսօր մեկնարկի համար առաձնացված 5
                նպատակային դասընթացների մասով արդեն իսկ ներգրավված են 50-ից
                ավելի անձիք, որոնց ստեղծարարության արդյունքը «Ազգային դպրոցի»
                նախնական բովանդակության առկայությունն է։ Այսպիսով, ազգային
                ինքնության, մշակութային ժառանգության պահպանության,
                արդիականացման, փոխանցման ամբողջ գործընթացի նպատակը կրթության
                միջոցով ազգի ապագայի կերտումն է, որի բանալիները երեքն են՝
                «Ազգային դպրոց», «Առաքինի հայ մարդ-քաղաքացի», «Ազգային
                ուսուցիչ»։
              </div>
            </div>
          ),
        },
        {
          title: "Հարց 4․ Ո՞րն է «Ազգային կրթակարգի» ստեղծման բուն նպատակը․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> 1.«Ազգային դպրոցի» (առայժմ՝ վիրտուալ
                միջավայրում գործող) ստեղծում 2․ 21-րդ դարի առաքինի, ազատ ու
                արդարամիտ հայ մարդու և քաղաքացու կայացման համար կրթական և
                դաստիարակչական գործառույթների լիարժեք ներդաշնակում: 3.Այս ամենի
                իրականացման համար անհրաժեշտ բացառիկ պատասխանատվությամբ և
                դերակատարությամբ «Ազգային ուսուցչի» կերպարի ձևավորում։
              </div>
            </div>
          ),
        },
        {
          title:
            "Հարց 5․ Ովքե՞ր են լինելու «Ազգային կրթական համակարգի» իրական շահառուները․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> «Ազգային կրթական համակարգը» կոչված է
                լրացնելու հանրակրթության ոլորտում առկա կրթական, դաստիարակչական և
                տեխնոլոգիական բացերը։ Հետևաբար, նրա շահառուները կրթական
                գործընթացի բոլոր կողմերն են՝ ուսուցիչները, աշակերտները,
                ծնողները, կրթությունը կազմակերպող պետական կառավարման ու տեղական
                ինքնակառավարման մարմինները, հանրակրթական դպրոցները և կրթություն
                իրականացնող մյուս հաստատությունները։
              </div>
            </div>
          ),
        },
        {
          title: "Հարց 6․ Ի՞նչ ֆորմալ հիմք ունի «Ազգային կրթական համակարգը»․",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> «Ազգային կրթական համակարգի» ֆորմալ
                հիմքը պետական գրանցում ստացած «Ազգային կրթություն» կրթական
                հիմնադրամն է, որի հոգաբարձուների խորհուրդը հանդես է գալիս որպես
                նույն «Ազգային կրթության խորհուրդ»՝ ամբողջ գործընթացի
                պատասխանատու։ ենք, որ հետագայում «Առարկայական մասնագիտական
                խմբերը» արդեն կգործեն կանոնակարգված՝ կրթական դրամաշնորհային
                ծրագրերի իրացման միջոցով։
              </div>
            </div>
          ),
        },
        {
          title:
            "Հարց 7․ «Ազգային կրթական համակարգը» հասարակական աջակցության ինչպիսի՞ ակնկալիք ունի և ո՞ր շերտերից։",
          content: (
            <div className="flex flex-col gap-8 my-8">
              <div className="ml-2 list-decimal text-black text-start font-noto-sans-armenian text-[18px] font-medium leading-normal">
                <strong>Պատասխան․</strong> Հասարակական աջակցության առնվազն երեք
                մակարդակ ենք ակնկալում․
              </div>
              <div className="ml-8">
                <ul className="pl-4 list-disc text-black text-start text-[18px] font-medium leading-normal">
                  <li>
                    Առաջինը՝ մասնագիտական շրջանակն է. կրթական և ՏՏ ոլորտի այն
                    մասնագետները, ովքեր անհաշտ են ազգային ինքնության ոչնչացման
                    այս գործընթացի հետ և ունակ են արարել հանրային բարիք:
                  </li>
                  <li>
                    Երկրորդը ազգային կապիտալը ներկայացնող շերտն է, ովքեր
                    գիտակցում են ազգի առջև ծառացած սպառնալիքները և հասկանում, որ
                    առանց ազգի ու պետության սեփական ունեցվածքն առ ոչինչ է՝ վկա
                    մեր պատմությունը։ Հետևաբար, նրանք կարող են և պետք է այսօր
                    ներդրումներ կատարեն հանուն ազգի վաղվա ապագայի։ Ֆորմալ
                    առումով՝ դրանք նվիրատվություններ են հիմնադրամին, որի
                    դրամաշնորհային գործունեությունն ամբողջապես բաց է լինելու։
                  </li>
                  <li>
                    Իսկ արդեն երրորդ մակարդակը հանրային լայն ու բազմաշերտ
                    աջակցությունն է, երբ խնդրի կարևորության հանրային գիտակցումը
                    անկասելի կդարձնի «Ազգային կրթական համակարգի» ձևավորումն ու
                    իրականացումը, կկանգնեցնի ազգային անկման այս աղետաբեր
                    գործընթացը։
                  </li>
                </ul>
              </div>
            </div>
          ),
        },
      ],
    },
    footer: {
      title: "«Ազգային Կրթություն» կրթական հիմնադրամ",
      copyright: "Բոլոր իրավունքները պաշտպանված են",
      currentYear: new Date().getFullYear(),
    },
    events: {
      heading: "Իրադարձություններ",
      list: [
        {
          title: "Հայաստանի քանդվող ապագան՝ հանրակրթության խեղումը",
          description:
            "📍«Ազգային կրթության խորհուրդը» հրավիրում է մասնակցելու հանրային զեկույց-աշխատաժողովի «Հայաստանի քանդվող ապագան՝ հանրակրթության խեղումը։ Այլընտրանքի հրամայականը»",
          date: "Մայիսի 7-ին, ժամը 16:00",
          location:
            "«Էլիտ Պլազա» բիզնես կենտրոնի Քրքորյան սրահ (ք. Երևան, Մ․ Խորենացի փ․ 15)",
          button: {
            title: "ՀԱՆՐԱՅԻՆ ԶԵԿՈՒՅՑ",
            link: "https://firebasestorage.googleapis.com/v0/b/donation-mitq.firebasestorage.app/o/1%E2%80%A4%20%D4%B6%D5%A5%D5%AF%D5%B8%D6%82%D5%B5%D6%81.pdf?alt=media&token=63caa3b8-9469-4d64-8d7f-412109b37623",
          },
          contact: "—",
        },
        {
          title: "Հայաստանի քանդվող ապագան` փակվող դպրոցներ",
          description:
            "Ազգային կրթության խորհուրդը ներկայացնում է հանրային զեկույց « Հայաստանի քանդվող ապագան` փակվող դպրոցներ » խորագրով և բոլոր մտահոգ անձանց հրավիրում է հանրային քննարկման։",
          date: "Փետերվարի 20-ին, ժամը 13:00",
          location:
            "Անի Պլազա հյուրանոցի Դվին սրահ (ք. Երևան  Ս. Նովայի պող., 19 շենք)",
          button: {
            title: "Ամփոփիչ հայտարարություն",
            link: "/CouncilAnnouncement",
          },
          isDisabled: true,
          contact: "—",
        },
        {
          title: "Հերոսն ու հերոսականությունը կրթության մեջ",
          description:
            "«Ազգային կրթության խորհուրդը» ազգային հերոսների ու նրանց հերոսականության արժևորումը կրթության բովանդակության մեջ համարում է հիմնաքարային՝ որպես ապագա սերնդի դաստիարակության գործում  ազգային առաքելության գիտակցման կարևոր հիմնասյուն։ ",
          date: "Նոյեմբերի 25, ժամը 19։00-ին",
          location:
            "Երևանի «Պատանի հանդիսատեսի թատրոնի» շենքում։ (ք. Երևան, Մոսկովյան 3)",
          contact: "094457773, 077090514",
          isDisabled: true,
        },
        {
          title: "Տե՛ր կանգնենք մեր լեզվին, մեր արժեքներին, մեր հայրենիքին",
          description: `Հարգելի հայրենակից,
մեր օրերում տիրող քաղաքական և հասարակական քաոսային վիճակը, դրանից բխող անհուսալիության և անտարբերության մթնոլորտը ստեղծել են հայ ազգային արժեքներին, մեր ինքնությանը սպառնացող մի իրավիճակ, երբ յուրաքանչյուր պատասխանատու անհատից պահանջվում է մի բան՝ անկախ քաղաքական, հասարակական, անձնական հայացքներից և հակակրանքներից, հավաքվել մեկ սեղանի շուրջ և հասկանալ, թե ինչպես  ենք ՏԵՐ կանգնում մեր տեսակին՝ հայի ինքնությանը, մեր ազգային իրավունքներին, մեր արժանապատվությանն ու հայրենիքին:
Բնության մեջ յուրաքանչյուր տեսակ ունի իր առաքելությունը, գոյության նպատակն ու իրավունքը, իր տեսակի պահպանության համար պայքարի անհրաժեշտությունը։`,
          date: "Հոկտեմբերի 4, ժամը 13։00",
          location: "Երևան, «Էլիտ Պլազա» բիզնես կենտրոն",
          contact: "—",
          isDisabled: true,
        },
        {
          title:
            "«Ազգային կրթության խորհրդի» կողմից փակ հանդիպում-աշխատաժողովի հրավեր",
          description:
            "Նպատակը՝ ազգային կրթական խնդիրներով մտահոգ կազմակերպությունների և անհատների միջև փոխճանաչելիության ապահովումն է և տեղեկատվության փոխանակման ու փոխգործակցության միասնական հնարավոր պլատֆորմի ձևավորումն է՝ հանուն «Ազգային կրթական համակարգի» կայացման և ազգային ինքությանը սպառնացող մարտահրավերների չեզոքացման։ Աշխատաժողովի օրակարգը բաց է և ուղղված է լինելու կրթական կազմակերպությունների գործունեության փոխճանաչելիության և փոխվստահության ապահովմանը։",
          date: "Սեպտեմբերի 25, ժամը 17:00, ավարտված",
          location:
            "Երևան, Ազատության պող. 24/10, «Հայոց Աշխարհաժողով» ԱԱՀ շարժման գրասենյակ",
          contact: "—",
          isDisabled: true,
        },
        {
          title: "Շուտով․ Փակ հանդիպում-աշխատաժողով պատմաբանների հետ",
          description:
            "Անհրաժեշտ է հանրակրթության ոլորտում Հայոց պատմության ուրացմանն այլընտրանքային ամբողջական լուծում։ «Ազգային կրթության խորհուրդը» նպատակ ունի մեկ միասնական մոտեցմամբ ներկայացնել Հայոց պատմությունը՝ որպես քաղաքակրթական առաքելության ամբողջական ճանապարհ։",
          date: "Շուտով",
          location: "—",
          contact: "—",
        },
        {
          title: "Ազգային Ուսուցիչ մրցանակաբաշխություն",
          description:
            "«Ազգային կրթության խորհուրդը» ներկայացնում է «Ազգային Ուսուցիչ» անվանակարգային մրցանակաբաշխությունը։",
          button: { title: "Իմանալ ավելին", link: "/award" },
          date: "2026թ. Մայիս 28",
          location: "Մանրամասները կհրապարակվեն։",
          contact: "—",
        },
      ],
    },
    openLetter: {
      heading: "«Ազգային կրթության խորհրդի» բաց նամակը՝",
      subheading:
        "2026-ի խորհրդարանական ընտրություններին մասնակցող քաղաքական ուժերին‼️",
      description:
        "🚩 «Ազգային կրթության խորհուրդը» 2026-ի հունիսին 7-ին կայանալիք խորհրդարանական ընտրությունները դուրս է համարում քաղաքական մրցապայքարի պարզ տարմաբանությունից, այն դիտարկելով հայ ազգի համար արժեհամակարգային և քաղաքակրթական ընտրության ճամփաբաժան։ Հետևաբար, խորհուրդը բաց նամակով դիմում է մասնակից քաղաքական ուժերին՝ հստակեցնելու նրանց տեսակետը գիտա-կրթական ոլորտի ճակատագրի վերաբերյալ` ակնկալով երեք պարզ հարցի հրապարակային պատասխան․\n\nԻշխանություն ունենալու պարագայում արդյո՞ք պատրաստ են՝",
      questions: [
        {
          letter: "Ա",
          text: "Հանրակրթության բովանդակությունը կառուցել ազգային-քաղաքակրթական արժեհամակարգի հենքով` ի տարբերություն ապազգայնացման ներկա միտումների,",
        },
        {
          letter: "Բ",
          text: "Երաշխավորել, որ ներկայում շրջանառության մեջ դրված 229 դպրոցների փակման ծրագիրը կչեղարկվի և դպրոցները չեն փակվի։",
        },
        {
          letter: "Գ",
          text: "Մշակել և իրականացնել բարձրագույն կրթության և գիտության առաջընթաց զարգացման ազգային ռազմավարություն։",
        },
      ],
      outro:
        "Հավելենք, որ բոլոր պատասխանները (ներառյալ պատասխանի բացակայությունը) հրապարակվելու են։",
      signature: "Հարգանքներով՝ «Ազգային կրթության խորհուրդ»",
      responsesTitle: "Մասնակից քաղաքական ուժերի պատասխանները",
      responses: [
        {
          orgName: "Ազգային Ժողովրդավարական Բևեռ (ԱԺԲ)",
          title:
            "«Ազգային Ժողովրդավարական Բևեռ» (ԱԺԲ) կուսակցության պաշտոնական պատասխանը",
          date: "27 Մայիսի, 2026թ.",
          status: "Ընդունված է (Այո բոլոր հարցերին)",
          intro:
            "Հարգելի Ազգային Կրթության Խորհուրդ,\n\nՁեր 2026 թվականի մայիսի 27-ի բաց նամակը ստացվել է: Ազգային Ժողովրդավարական Բևեռի (ԱԺԲ) Խորհուրդը ուշադիր կարդացել է երեք հարցերը և ներկայացնում է իր պաշտոնական դիրքորոշումը ստորև: Բոլոր երեք հարցերին մեր պատասխանն է՝ Այո:",
          answers: [
            {
              question:
                "Ա․ Իշխանություն ունենալու պարագայում արդյո՞ք պատրաստ ենք՝ հանրակրթության բովանդակությունը կառուցել ազգային-քաղաքակրթական արժեհամակարգի հենքով` ի տարբերություն ապազգայնացման ներկա միտումների,",
              reply: "Այո՝ առանց վերապահման:",
              details:
                "ԱԺԲ-ն կարծում է, որ հայկական դպրոցը կոչված է ձևավորելու ազգային-քաղաքակրթական բնույթ: Հայկական կրթությունը պետք է արմատավորված լինի Հայ Առաքելական ավանդույթների, հայ ազգային պատմության և հայ մշակութային ինքնության մեջ: Հայ երեխաները դպրոցում պետք է սովորեն, որ Արցախը իրենց հայրենիքն է, որ Հայերի ցեղասպանության համաշխարհային ճանաչումը փաստաթղթավորված, հաստատված պատմական փաստ է, և որ Մայր Աթոռ Սուրբ Էջմիածինը հայ ինքնության անբաժանելի, կրող հենասյուն է ու որևէ քաղաքական գաղափարախոսությամբ չի կարող փոխարինվել: Ազգային-քաղաքացիական արժեքների ձևավորումը նշանակում է, որ հայ երեխան ավարտում է որպես լիարժեք քաղաքացի՝ կողմնորոշված դեպի հայրենիքը և ինքնիշխան ինքնորոշումը: Ցանկացած ուսումնական ծրագիր, որը ջնջում է Արցախը, լուսանցք է մղում Ցեղասպանությունը կամ Մայր Աթոռը դարձնում է հավասարների մեջ հերթական հաստատություն, դա ազգային ուսումնական ծրագիր չէ՝ դա ապազգայնացման ծրագիր է: ԱԺԲ-ն դա թույլ չի տա:",
            },
            {
              question:
                "Բ․ Իշխանություն ունենալու պարագայում արդյո՞ք պատրաստ ենք՝ երաշխավորել, որ ներկայում շրջանառության մեջ դրված 229 դպրոցների փակման ծրագիրը կչեղարկվի և դպրոցները չեն փակվի։",
              reply: "Այո՝ 229 դպրոց փակելու ծրագիրը պետք է չեղյալ հայտարարվի։",
              details:
                "Գյուղական և շրջանային համայնքների դպրոցները օպտիմալացման ենթակա ծախս չեն: Դրանք այդ համայնքների գոյատևման նախապայմանն են: Գյուղական դպրոցի փակումը արագացնում է բնակչության թվի նվազումը, իսկ բնակչության թվի նվազումը չի կարող պաշտպանվել կամ կառավարվել: ԱԺԲ-ն դպրոցական ենթակառուցվածքները դիտարկում է որպես ռազմավարական ազգային ենթակառուցվածքներ՝ նույն մակարդակի վրա, ինչ ճանապարհները, սահմանային կետերը և ջրային համակարգերը: Մենք կչեղարկենք 229 դպրոցների փակման ծրագիրը և կապահովենք, որ գյուղական դպրոցների պահպանումը ֆինանսավորվի որպես ազգային անվտանգության պարտավորություն, այլ ոչ թե սոցիալական ապահովության հայեցողություն։",
            },
            {
              question:
                "Գ․ Իշխանություն ունենալու պարագայում արդյո՞ք պատրաստ ենք՝ մշակել և իրականացնել բարձրագույն կրթության և գիտության առաջընթաց զարգացման ազգային ռազմավարություն։",
              reply: "Այո՝ Հայաստանի ինքնիշխանությունն է դա պահանջում։",
              details:
                "1. Մի երկիր, որը չի կարող կրեն իր սեփական ինժեներներին, գիտնականներին, կախված է դառնում ուրիշներից՝ իր սեփական տարածքը կառավարող գիտելիքների համար: Հայաստանի ինքնիշխան պետական կարողությունները հիմնված են իր սեփական գիտական, տեխնիկական և մտավոր հաստատությունների որակի վրա: ԱԺԲ-ն պարտավորվում է մշակել և իրականացնել բարձրագույն կրթության և գիտության ազգային ռազմավարություն, որը հայկական համալսարաններն ու հետազոտական հաստատությունները դիտարկում են որպես ինքնիշխան պետական կարողությունների հենասյուներ, կառուցում են ֆինանսավորման մեխանիզմներ՝ անկախ մեկ դոնորի կախվածությունից, և բարձրագույն կրթության քաղաքականությունը ուղղակիորեն կապում են Հայաստանի ազգային անվտանգության և տնտեսական ինքնաբավության հետ: Այս ռազմավարությունը պետք է արհեստական բանականության կրթությունը դնի կենտրոնում. մի ազգ, որը չի պատրաստում իր սեփական արհեստական բանականության մասնագետներին և չի կառավարում արհեստական բանականության զարգացումը իր սեփական պայմաններով, ինքնիշխանության որոշիչ չափանիշը զիջում է ուրիշներին: Հայաստանը պետք է այս կարողությունները զարգացնի երկրի ներսում, այլ ոչ թե ներմուծի այն ուրիշների պայմաններով։",
            },
          ],
          outro:
            "ԱԺԲ Խորհուրդը ողջունում է Կրթության ազգային խորհրդի նախաձեռնությունը՝ այս հարցը հրապարակայնորեն ուղղելու ընտրություններին մասնակից բոլոր քաղաքական ուժերին: Ձեր կողմից բարձրացված երեք հարցերը վարչական հարցեր չեն: Դրանք հարցեր են այն մասին, թե արդյոք հաջորդ խորհրդարանը հասկանում է, որ ազգի դպրոցը նրա շարունակական գոյության հիմքն է որպես ազգ։\n\nՀավատք, Հայրենիք, Ինքնիշխանություն — Հայաստանը սակարկման ենթակա չէ։",
          signature:
            "Հարգանքներով՝\nԱզգային Ժողովրդավարական Բևեռի (ԱԺԲ) Խորհուրդ\nԵրևան, 27 Մայիսի, 2026թ.",
        },
        {
          orgName: "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինք",
          title:
            "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինքի պատասխանը «Ազգային կրթության խորհրդի» բաց նամակին",
          date: "28 Մայիսի, 2026թ.",
          status: "Ընդունված է (Այո բոլոր հարցերին)",
          intro:
            "Հարգելի՛ «Ազգային կրթության խորհրդի» անդամներ,\n\nՇնորհակալություն ենք հայտնում կրթության և գիտության ոլորտի ապագայի վերաբերյալ հանրային կարևոր քննարկում նախաձեռնելու համար։ «ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինքը լիովին կիսում է այն համոզմունքը, որ Հայաստանի Հանրապետության զարգացումն անհնար է առանց ազգային արժեքների վրա հիմնված, մրցունակ և ապագային միտված կրթական ու գիտական համակարգի։ Ձեր բարձրացրած հարցերին պատասխանում ենք հետևյալ կերպ․",
          answers: [
            {
              question:
                "Ա. Հանրակրթության բովանդակության ազգային-քաղաքակրթական հիմների վերաբերյալ",
              reply: "Այո՛։",
              details:
                "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինքը համոզված է, որ հանրակրթության բովանդակությունը պետք է ձևավորվի հայ ժողովրդի ազգային ինքնության, պատմական հիշողության, մշակութային ժառանգության, պետականության և քաղաքակրթական առաքելության գիտակցման հիման վրա։ Մենք կարևորում ենք համամարդկային արժեքների և ժամանակակից գիտելիքների համադրումը ազգային արժեհամակարգի հետ՝ բացառելով կրթության ապազգայնացման ցանկացած դրսևորում։ Հայ դպրոցը պետք է դաստիարակի գիտակից, հայրենասեր, պատասխանատու և զարգացած քաղաքացի։",
            },
            {
              question: "Բ. Դպրոցների փակման ծրագրի վերաբերյալ",
              reply: "Այո՛։",
              details:
                "Դաշինքը դեմ է դպրոցների մեխանիկական փակման քաղաքականությանը, հատկապես սահմանամերձ, գյուղական և փոքր համայնքներում, որտեղ դպրոցը ոչ միայն կրթական հաստատություն է, այլև համայնքային կյանքի, մշակույթի և ազգային անվտանգության կարևոր բաղադրիչ։ Իշխանություն ձևավորելու պարագայում մենք կվերանայենք ներկայում շրջանառության մեջ գտնվող 229 դպրոցների փակման ծրագիրը և կապահովենք, որ յուրաքանչյուր որոշում կայացվի բացառապես կրթության որակի բարելավման, երեխաների շահերի և համայնքների կենսունակության պահպանման սկզբունքներով, հաշվի առնելով տվյալ բնակավայրի քաղաքացիների կարծիքը։",
            },
            {
              question:
                "Գ. Բարձրագույն կրթության և գիտության ազգային ռազմավարության վերաբերյալ",
              reply: "Այո՛։",
              details:
                "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինքը առաջնահերթ է համարում բարձրագույն կրթության և գիտության ոլորտների արմատական զարգացումը։ Մենք նախաձեռնելու ենք կրթության, գիտության, նորարարության և տեխնոլոգիական զարգացման երկարաժամկետ ազգային ռազմավարության մշակում և իրականացում՝ ուղղված հայկական բուհերի մրցունակության բարձրացմանը, գիտական ներուժի պահպանմանն ու ընդլայնմանը, երիտասարդ գիտնականների խրախուսմանը, ինչպես նաև գիտության և տնտեսության միջև արդյունավետ կապի ձևավորմանը։ Համոզված ենք, որ գիտելիքահենք տնտեսության և պետության կառուցումը Հայաստանի անվտանգության, զարգացման և միջազգային մրցունակության կարևորագույն նախապայմաններից է։",
            },
          ],
          outro:
            "Վստահ ենք, որ կրթության և գիտության շուրջ համազգային համաձայնության ձևավորումը մեր պետականության ամրապնդման կարևորագույն գրավականներից է։",
          signature: "Հարգանքներով՝\n«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ» դաշինք",
        },
        {
          orgName: "«Հայ Ազգային Կոնգրես» կուսակցություն",
          title:
            "«Հայ Ազգային Կոնգրես» կուսակցության պատասխանը «Ազգային կրթության խորհրդի» բաց նամակին",
          date: "29 Մայիսի, 2026թ.",
          status: "Ընդունված է (Այո / Համահունչ)",
          intro:
            "Ի պատասխան 2026թ հունիսի 7-ի խորհրդարանական ընտրություններին մասնակցող քաղաքական ուժերին ուղղված ձեր բաց նամակի, ներկայացնում ենք Հայ Ազգային Կոնգրեսի դիրքորոշումը։",
          answers: [
            {
              question:
                "Ա. Հանրակրթության բովանդակության ազգային-քաղաքակրթական հիմների վերաբերյալ",
              reply: "Այո՛։",
              details:
                "Հանրակրթության բովանդակությունը կառուցել ազգային և համամարդկային արժեհամակարգերի հենքով՝ ի տարբերություն ազգային արժեքների ոտնահարման ներկա միտումների։",
            },
            {
              question: "Բ. Դպրոցների փակման ծրագրի վերաբերյալ",
              reply: "Այո՛ (Համահունչ)։",
              details:
                "Ձեր տեսակետները լիովին համահունչ են ՀԱԿ-ի ծրագրային մոտեցումներին, որոնց մանրամասն կարող եք ծանոթանալ կուսակցության anc.am կայքում զետեղված նախընտրական ծրագրի «Կրթություն» բաժնում։",
            },
            {
              question:
                "Գ. Բարձրագույն կրթության և գիտության ազգային ռազմավարության վերաբերյալ",
              reply: "Այո՛ (Համահունչ)։",
              details:
                "Ձեր տեսակետները լիովին համահունչ են ՀԱԿ-ի ծրագրային մոտեցումներին, որոնց մանրամասն կարող եք ծանոթանալ կուսակցության anc.am կայքում զետեղված նախընտրական ծրագրի «Կրթություն» բաժնում։",
            },
          ],
          outro: "Շնորհակալ ենք ձեր մոտեցումների և մտահոգությունների համար։",
          signature: "Հարգանքներով՝\nՀայ Ազգային Կոնգրես",
        },
      ],
    },
  },
};

const Header = ({
  lang,
  setLang,
  mobileMenuOpen,
  setMobileMenuOpen,
  t,
  handleNavClick,
  currentUser,
}) => (
  <header className="glass-header sticky top-0 z-50">
    <nav className="container mx-auto px-6 py-2 flex justify-between items-center">
      <a href="#home" onClick={(e) => handleNavClick(e, "home")}>
        <img
          src="donation.png"
          alt="National Education Foundation Logotype"
          className="h-16 md:h-20 w-auto"
        />
      </a>
      <div className="hidden md:flex items-center space-x-4 font-medium text-sm">
        <a
          href="#home"
          className="text-brand-dark hover:text-brand-gold transition-colors"
          onClick={(e) => handleNavClick(e, "home")}
        >
          {t.nav.home}
        </a>
        <a
          href="#about"
          className="text-brand-dark hover:text-brand-gold transition-colors"
          onClick={(e) => handleNavClick(e, "about")}
        >
          {t.nav.about}
        </a>
        <a
          href="#open-letter"
          className="text-brand-dark hover:text-brand-gold transition-colors font-noto-sans-armenian"
          onClick={(e) => handleNavClick(e, "open-letter")}
        >
          {t.nav.openLetter}
        </a>
        <a
          href="#events"
          className="text-brand-dark hover:text-brand-gold transition-colors"
          onClick={(e) => handleNavClick(e, "events")}
        >
          {t.nav.events}
        </a>
        <a
          href="#donate"
          className="text-brand-dark hover:text-brand-gold transition-colors"
          onClick={(e) => handleNavClick(e, "donate")}
        >
          {t.nav.donate}
        </a>
        <a
          href="#contact"
          className="text-brand-dark hover:text-brand-gold transition-colors"
          onClick={(e) => handleNavClick(e, "contact")}
        >
          {t.nav.contact}
        </a>

        {currentUser && (
          <a
            href="/dashboard"
            className="ml-4 px-4 py-2 bg-[#D9A443] hover:bg-[#E8BB68] text-white font-bold rounded transition-colors"
          >
            Գնալ Կառավարման Պատուհան
          </a>
        )}
      </div>
      <div className="flex items-center">
        <div className="lang-switcher bg-white/50 rounded-full p-1 flex shadow-inner">
          <button
            onClick={() => setLang("en")}
            className={`px-3 py-1 text-xs rounded-full ${lang === "en" ? "active" : ""}`}
          >
            EN
          </button>
          <button
            onClick={() => setLang("hy")}
            className={`px-3 py-1 text-xs rounded-full ${lang === "hy" ? "active" : ""}`}
          >
            HY
          </button>
        </div>
        <button
          id="mobile-menu-button"
          className="md:hidden ml-4 p-2 rounded-md text-brand-dark"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 6h16M4 12h16m-7 6h7"
            ></path>
          </svg>
        </button>
      </div>
    </nav>
    <div
      id="mobile-menu"
      className={`${mobileMenuOpen ? "" : "hidden"} md:hidden px-6 pb-4 bg-white/80`}
    >
      <a
        href="#home"
        className="block py-2 text-brand-dark hover:text-brand-gold"
        onClick={(e) => handleNavClick(e, "home")}
      >
        {t.nav.home}
      </a>
      <a
        href="#about"
        className="block py-2 text-brand-dark hover:text-brand-gold"
        onClick={(e) => handleNavClick(e, "about")}
      >
        {t.nav.about}
      </a>
      <a
        href="#open-letter"
        className="block py-2 text-brand-dark hover:text-brand-gold font-noto-sans-armenian"
        onClick={(e) => handleNavClick(e, "open-letter")}
      >
        {t.nav.openLetter}
      </a>
      <a
        href="#events"
        className="block py-2 text-brand-dark hover:text-brand-gold"
        onClick={(e) => handleNavClick(e, "events")}
      >
        {t.nav.events}
      </a>
      <a
        href="#donate"
        className="block py-2 text-brand-dark hover:text-brand-gold"
        onClick={(e) => handleNavClick(e, "donate")}
      >
        {t.nav.donate}
      </a>
      <a
        href="#contact"
        className="block py-2 text-brand-dark hover:text-brand-gold"
        onClick={(e) => handleNavClick(e, "contact")}
      >
        {t.nav.contact}
      </a>
      {currentUser && (
        <a
          href="/dashboard"
          className="block mt-2 py-2 bg-[#D9A443] hover:bg-[#E8BB68] text-white font-bold text-center rounded"
        >
          Գնալ Կառավարման Պատուհան
        </a>
      )}
    </div>
  </header>
);

const HomeSection = ({ t, handleNavClick }) => {
  const isArmenian = t.nav.about === "Մեր մասին";

  return (
    <section
      id="home"
      className="relative text-white min-h-screen flex items-center justify-center py-24 md:py-32 overflow-hidden"
    >
      {/* Background with modern premium styling */}
      <div className="absolute inset-0 bg-brand-dark">
        <img
          src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop"
          alt="Inspiring educational scene"
          className="w-full h-full object-cover opacity-25 scale-105 transition-transform duration-10000 ease-out"
        />
        {/* Sleek radial overlay to focus text */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/90 to-brand-dark/40" />
      </div>

      {/* Modern floating blur blobs */}
      <div className="absolute top-1/4 left-1/10 w-72 h-72 bg-brand-gold/10 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 right-1/10 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 container mx-auto px-6 max-w-5xl reveal">
        <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-[36px] p-8 md:p-16 shadow-2xl relative overflow-hidden">
          {/* Subtle top golden light bar */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-brand-gold to-transparent" />

          <div className="text-center mb-8">
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-gold/25 border border-brand-gold/45 text-brand-gold text-xs font-semibold tracking-widest uppercase mb-6">
              {isArmenian ? "ԱԶԳԱՅԻՆ ԿՐԹՈՒԹՅՈՒՆ" : "NATIONAL EDUCATION"}
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight font-noto-sans-armenian tracking-tight">
              {isArmenian ? (
                <>
                  «Ազգային կրթության խորհրդի»{" "}
                  <span className="text-brand-gold">Բաց Նամակը</span>
                </>
              ) : (
                <>
                  <span className="text-brand-gold">Open Letter</span> of the
                  National Education Council
                </>
              )}
            </h1>
            <p className="mt-2 text-brand-gold text-lg md:text-xl font-medium uppercase tracking-wider font-noto-sans-armenian">
              {isArmenian
                ? "2026-ի խորհրդարանական ընտրություններին"
                : "For the 2026 Parliamentary Elections"}
            </p>
          </div>

          <div className="text-base md:text-lg text-gray-300 leading-relaxed font-noto-sans-armenian mb-10 text-justify bg-white/5 p-6 rounded-2xl border border-white/5">
            {isArmenian ? (
              <>
                🚩 <strong>«Ազգային կրթության խորհուրդը»</strong> 2026-ի
                հունիսին 7-ին կայանալիք խորհրդարանական ընտրությունները դուրս է
                համարում քաղաքական մրցապայքարի պարզ տրամաբանությունից՝ այն
                դիտարկելով հայ ազգի համար արժեհամակարգային և քաղաքակրթական
                ընտրության ճամփաբաժան: Խորհուրդը բաց նամակով դիմում է մասնակից
                քաղաքական ուժերին՝ հստակեցնելու նրանց տեսակետը գիտա-կրթական
                ոլորտի ճակատագրի վերաբերյալ՝ ակնկալելով երեք պարզ հարցի
                հրապարակային պատասխան:
              </>
            ) : (
              <>
                🚩 The <strong>'National Education Council'</strong> considers
                the upcoming parliamentary elections on June 7, 2026, as a
                turning point of value systems and civilization for the Armenian
                nation. Consequently, the Council addresses the participating
                political forces with an open letter to clarify their views on
                the future of the science and education sectors, expecting
                public responses to three vital questions.
              </>
            )}
          </div>

          {/* Action buttons with gorgeous transitions */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-12">
            <a
              href="#open-letter"
              onClick={(e) => handleNavClick(e, "open-letter")}
              className="w-full sm:w-auto px-8 py-4 bg-brand-gold hover:bg-brand-gold-dark text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg shadow-brand-gold/20 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              {isArmenian ? "Կարդալ Կայքում" : "Read on Website"}
            </a>
            <a
              href="/files/ԲԱՑ ՆԱՄԱԿ.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg
                className="w-5 h-5 text-brand-gold"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {isArmenian
                ? "Ներբեռնել Բաց Նամակը (PDF)"
                : "Download Open Letter (PDF)"}
            </a>
          </div>

          {/* Parties response shortcuts */}
          <div className="border-t border-white/10 pt-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-6 font-noto-sans-armenian text-center">
              {isArmenian
                ? "ՔԱՂԱՔԱԿԱՆ ՈՒԺԵՐԻ ՊԱՏԱՍԽԱՆՆԵՐԸ"
                : "RESPONSES FROM POLITICAL FORCES"}
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              {/* NDP / ԱԺԲ */}
              <a
                href="/files/Ազգային Ժողովրդավարական Բևեռի (ԱԺԲ).pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white/5 hover:bg-white/10 border border-white/15 hover:border-brand-gold/50 rounded-2xl p-4 transition-all duration-300 text-center flex flex-col justify-between items-center"
              >
                <span className="text-xs text-brand-gold font-bold uppercase tracking-wide block mb-1">
                  {isArmenian ? "ԱԺԲ ՊԱՏԱՍԽԱՆ" : "NDP RESPONSE"}
                </span>
                <p className="text-xs text-gray-300 group-hover:text-white transition-colors leading-relaxed font-noto-sans-armenian font-semibold mb-3">
                  {isArmenian
                    ? "Ազգային Ժողովրդավարական Բևեռ"
                    : "National Democratic Pole"}
                </p>
                <div className="text-[11px] font-bold text-brand-gold group-hover:underline flex items-center gap-1">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  PDF (52.2 KB)
                </div>
              </a>

              {/* STRONG ARMENIA / ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ */}
              <a
                href="/files/strong-education.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white/5 hover:bg-white/10 border border-white/15 hover:border-brand-gold/50 rounded-2xl p-4 transition-all duration-300 text-center flex flex-col justify-between items-center"
              >
                <span className="text-xs text-brand-gold font-bold uppercase tracking-wide block mb-1">
                  {isArmenian ? "«ՈՒԺԵՂ ՀԱՅԱՍՏԱՆ»" : "STRONG ARMENIA"}
                </span>
                <p className="text-xs text-gray-300 group-hover:text-white transition-colors leading-relaxed font-noto-sans-armenian font-semibold mb-3">
                  {isArmenian
                    ? "«Ուժեղ Հայաստան» դաշինք"
                    : "'Strong Armenia' Alliance"}
                </p>
                <div className="text-[11px] font-bold text-brand-gold group-hover:underline flex items-center gap-1">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  PDF (23.8 KB)
                </div>
              </a>

              {/* ANC / ՀԱԿ */}
              <a
                href="/files/Հայ Ազգային Կոնգրես.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative bg-white/5 hover:bg-white/10 border border-white/15 hover:border-brand-gold/50 rounded-2xl p-4 transition-all duration-300 text-center flex flex-col justify-between items-center"
              >
                <span className="text-xs text-brand-gold font-bold uppercase tracking-wide block mb-1">
                  {isArmenian ? "ՀԱԿ ՊԱՏԱՍԽԱՆ" : "ANC RESPONSE"}
                </span>
                <p className="text-xs text-gray-300 group-hover:text-white transition-colors leading-relaxed font-noto-sans-armenian font-semibold mb-3">
                  {isArmenian
                    ? "Հայ Ազգային Կոնգրես"
                    : "Armenian National Congress"}
                </p>
                <div className="text-[11px] font-bold text-brand-gold group-hover:underline flex items-center gap-1">
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    ></path>
                  </svg>
                  PDF (18.1 KB)
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Animated scroll down mouse indicator */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none select-none">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center p-1">
          <div className="w-1.5 h-3 bg-brand-gold rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

const AboutSection = ({ t }) => {
  return (
    <>
      <section id="about" className="pt-20 md:pt-32">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                {t.about.heading}
              </h2>
              <p className="mb-4 text-lg text-gray-600">{t.about.text1}</p>
              <p className="text-lg text-gray-600">{t.about.text2}</p>
            </div>
            <div className="reveal bg-white p-10 rounded-lg shadow-xl">
              <h3 className="text-3xl font-bold mb-6 text-brand-gold">
                {t.about.governance}
              </h3>
              <div className="space-y-6">
                <div>
                  <strong className="block text-xl text-brand-dark">
                    {t.about.trustees.title}
                  </strong>
                  <p className="text-gray-600">
                    {t.about.trustees.description}
                  </p>
                </div>
                <div>
                  <strong className="block text-xl text-brand-dark">
                    {t.about.director.title}
                  </strong>
                  <p className="text-gray-600">
                    {t.about.director.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

const SlidesSection = ({ lang }) => (
  <section id="activities" className="pt-10 md:pt-10 bg-white">
    <Slider lang={lang} />
  </section>
);

const OurWorkSection = ({ t }) => {
  useEffect(() => {
    const reveals = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible"); // ✅ matches your CSS
            observer.unobserve(entry.target); // animate once
          }
        });
      },
      { threshold: 0.1 },
    );

    reveals.forEach((el) => observer.observe(el));

    // cleanup on unmount / language switch
    return () => {
      reveals.forEach((el) => observer.unobserve(el));
    };
  }, [t]);

  return (
    <section id="activities" className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto reveal">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.work.heading}
          </h2>
          <p className="text-lg text-gray-600 mb-12">{t.work.subheading}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.work.areas.map((area, index) => (
            <div
              key={area}
              className="reveal bg-brand-off-white p-8 rounded-lg text-center transition-all duration-300 hover:shadow-md"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <h4 className="font-bold text-2xl mb-2 text-brand-dark">
                {area}
              </h4>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DonateSection = ({ t }) => (
  <section id="donate" className="py-20 md:py-32 bg-brand-dark text-white">
    <div className="container mx-auto px-6">
      <div className="grid md:grid-cols-2 gap-16 items-center">
        <div className="reveal">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t.donate.heading}
          </h2>
          <p className="text-gray-300 text-lg">{t.donate.subheading}</p>
        </div>
        <div className="reveal bg-white/10 p-8 rounded-lg shadow-2xl backdrop-blur-md border border-white/20">
          <h3 className="relative text-3xl font-bold text-center mb-6 text-white">
            {t.donate.formTitle}
            <span className="absolute -top-6 bg-[#D9A23B] p-2 rounded-2xl text-xs animate-pulse">
              {t.donate.comingSoon}
            </span>
          </h3>
          <form onSubmit={(e) => e.preventDefault()}>
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-2 text-gray-300"
                htmlFor="amount"
              >
                {t.donate.amountLabel}
              </label>
              <input
                disabled
                type="number"
                id="amount"
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-md focus:ring-brand-gold focus:border-brand-gold text-white placeholder-gray-400"
                placeholder="50"
              />
            </div>
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-2 text-gray-300"
                htmlFor="name"
              >
                {t.donate.nameLabel}
              </label>
              <input
                disabled
                type="text"
                id="name"
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-md focus:ring-brand-gold focus:border-brand-gold text-white"
              />
            </div>
            <div className="mb-6">
              <label
                className="block text-sm font-medium mb-2 text-gray-300"
                htmlFor="email"
              >
                {t.donate.emailLabel}
              </label>
              <input
                disabled
                type="email"
                id="email"
                className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-md focus:ring-brand-gold focus:border-brand-gold text-white"
              />
            </div>
            <button
              disabled
              type="submit"
              className="w-full bg-brand-gold hover:bg-brand-gold-dark text-white font-bold py-4 px-4 rounded-md transition-all duration-300 transform hover:scale-105"
            >
              {t.donate.button}
            </button>
          </form>
        </div>
      </div>
    </div>
  </section>
);

const ContactSection = ({ t }) => (
  <section id="contact" className="pb-20 p-8">
    <div className="container bg-white mx-auto py-12 px-6 text-center reveal border rounded-[24px] p-16">
      <h2 className="text-4xl md:text-5xl font-bold mb-6">
        {t.contact.heading}
      </h2>
      <p className="text-lg text-gray-600 mb-4">{t.contact.subheading}</p>
      <p className="font-semibold text-brand-gold text-xl">{t.contact.email}</p>
      <p className="mt-4 text-gray-500">{t.contact.address}</p>
    </div>
  </section>
);

const OpenLetterSection = ({ t }) => {
  const { openLetter } = t;
  const [activeResponse, setActiveResponse] = useState(null);

  if (!openLetter) return null;

  return (
    <section
      id="open-letter"
      className="py-20 md:py-32 bg-brand-off-white text-brand-dark"
    >
      <div className="container mx-auto px-6 max-w-5xl reveal">
        <div className="bg-white border-2 border-brand-gold/30 rounded-[32px] p-8 md:p-16 shadow-2xl relative overflow-hidden transition-all duration-300 hover:border-brand-gold">
          {/* Subtle gold decorative background glow */}
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />

          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-brand-gold/10 text-brand-gold text-sm font-semibold tracking-wider uppercase mb-4 animate-pulse">
              {t.nav.about === "Մեր մասին" ? "ԲԱՑ ՆԱՄԱԿ" : "OPEN LETTER"}
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-4 leading-tight font-noto-sans-armenian">
              {openLetter.heading}
            </h2>
            <p className="text-lg md:text-2xl font-semibold text-brand-gold font-noto-sans-armenian max-w-3xl mx-auto leading-relaxed">
              {openLetter.subheading}
            </p>
          </div>

          <div className="text-lg text-gray-700 leading-relaxed font-noto-sans-armenian mb-12 text-justify bg-brand-off-white/50 p-6 md:p-8 rounded-2xl border border-gray-100">
            {openLetter.description.split("\n\n").map((paragraph, idx) => (
              <p
                key={idx}
                className={idx > 0 ? "mt-4 font-bold text-brand-dark" : ""}
              >
                {paragraph}
              </p>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-3 mb-12">
            {openLetter.questions.map((q, idx) => (
              <div
                key={idx}
                className="bg-brand-dark text-white rounded-[24px] p-8 relative flex flex-col justify-between transition-all duration-300 hover:shadow-xl hover:-translate-y-2 border border-white/10 overflow-hidden"
              >
                {/* Floating large letter background */}
                <div className="absolute -right-4 -bottom-6 text-9xl font-extrabold text-white/5 select-none font-noto-sans-armenian">
                  {q.letter}
                </div>

                <div>
                  <div className="w-12 h-12 rounded-[16px] bg-[#d9a443] flex items-center justify-center text-black font-bold text-xl mb-6 shadow-md font-noto-sans-armenian">
                    {q.letter}
                  </div>
                  <p className="text-lg font-medium leading-relaxed font-noto-sans-armenian relative z-10">
                    {q.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-gray-600 font-medium italic text-center md:text-left font-noto-sans-armenian max-w-xl">
              {openLetter.outro}
            </p>
            <div className="text-right">
              <p className="font-bold text-brand-gold text-lg md:text-xl font-noto-sans-armenian">
                {openLetter.signature}
              </p>
            </div>
          </div>
        </div>

        {/* Responses from political forces */}
        {openLetter.responses && openLetter.responses.length > 0 && (
          <div className="mt-16 reveal">
            <h3 className="text-2xl md:text-4xl font-bold text-center text-brand-dark mb-8 font-noto-sans-armenian">
              {openLetter.responsesTitle}
            </h3>
            <div className="space-y-6">
              {openLetter.responses.map((response, rIdx) => {
                const isOpen = activeResponse === rIdx;
                return (
                  <div
                    key={rIdx}
                    className="border border-brand-gold/20 rounded-[24px] overflow-hidden shadow-lg transition-all duration-300 bg-white"
                  >
                    <button
                      onClick={() => setActiveResponse(isOpen ? null : rIdx)}
                      className="w-full flex flex-col md:flex-row justify-between items-start md:items-center p-6 md:p-8 bg-brand-dark text-white font-noto-sans-armenian transition-colors hover:bg-brand-dark/95"
                    >
                      <div className="text-left">
                        <span className="text-xs text-brand-gold font-semibold uppercase tracking-wider block mb-1 font-noto-sans-armenian">
                          {response.date}
                        </span>
                        <h4 className="text-lg md:text-xl font-bold font-noto-sans-armenian">
                          {response.orgName}
                        </h4>
                      </div>
                      <div className="flex items-center gap-4 mt-4 md:mt-0">
                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-[#d9a443]/20 text-[#d9a443] border border-[#d9a443]/30 font-noto-sans-armenian">
                          {response.status}
                        </span>
                        <div className="w-10 h-10 rounded-[12px] bg-[#d9a443] text-black flex items-center justify-center font-bold text-xl font-noto-sans-armenian">
                          {isOpen ? "−" : "+"}
                        </div>
                      </div>
                    </button>
                    {isOpen && (
                      <div className="p-6 md:p-8 font-noto-sans-armenian text-brand-dark animate-fadeIn">
                        <div className="text-gray-700 leading-relaxed mb-8 border-l-4 border-[#d9a443] pl-4 italic text-justify">
                          {response.intro
                            .split("\n\n")
                            .map((paragraph, idx) => (
                              <p key={idx} className={idx > 0 ? "mt-4" : ""}>
                                {paragraph}
                              </p>
                            ))}
                        </div>

                        <div className="space-y-6 mb-8">
                          {response.answers.map((ans, aIdx) => (
                            <div
                              key={aIdx}
                              className="bg-brand-off-white/50 rounded-2xl border border-gray-100 p-6"
                            >
                              <h5 className="font-bold text-gray-800 text-lg mb-3">
                                {ans.question}
                              </h5>
                              <div className="inline-block px-3 py-1 rounded bg-green-50 text-green-700 border border-green-200 text-sm font-bold mb-3">
                                {ans.reply}
                              </div>
                              <p className="text-gray-600 leading-relaxed text-justify text-base">
                                {ans.details}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="bg-brand-dark text-white p-6 md:p-8 rounded-2xl relative overflow-hidden">
                          <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-brand-gold/5 rounded-full blur-2xl" />
                          <div className="relative z-10 text-justify text-gray-200 leading-relaxed whitespace-pre-line italic mb-6">
                            {response.outro}
                          </div>
                          <div className="relative z-10 text-right border-t border-white/10 pt-4">
                            <p className="font-bold text-brand-gold leading-relaxed whitespace-pre-line">
                              {response.signature}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const QuestionsSection = ({ t }) => (
  <section id="questions" className="py-20 md:py-32">
    <div className="container mx-auto px-6 text-center reveal">
      <h2 className="text-2xl md:text-4xl font-bold mb-8">{t.FAQ.heading}</h2>
      {t.FAQ.accordions.map((accordion, index) => (
        <Accordion key={index} item={accordion} />
      ))}
    </div>
  </section>
);

const Footer = ({ t, currentYear }) => (
  <footer className="bg-brand-dark text-white py-10">
    <div className="container mx-auto px-6 text-center text-gray-400">
      <p>{t.footer.title}</p>
      <p className="mt-2">
        &copy; {currentYear} {t.footer.copyright}
      </p>
    </div>
  </footer>
);

export default function NationalEducation() {
  const { lang, setLang } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentUser = useCurrentUser();

  useEffect(() => {
    const revealElements = document.querySelectorAll(".reveal");
    const revealOnScroll = () => {
      const windowHeight = window.innerHeight;
      revealElements.forEach((el) => {
        const elementTop = el.getBoundingClientRect().top;
        if (elementTop < windowHeight - 100) {
          el.classList.add("visible");
        }
      });
    };
    window.addEventListener("scroll", revealOnScroll);
    revealOnScroll();

    return () => window.removeEventListener("scroll", revealOnScroll);
  }, []);

  useEffect(() => {
    trackEvent("page_view_home", {
      page_title: "National Education Foundation Home",
      language: lang,
    });
  }, [lang]);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    trackEvent("navigation_click", {
      section_id: targetId,
      language: lang,
    });
    setMobileMenuOpen(false);
    document.getElementById(targetId).scrollIntoView({ behavior: "smooth" });
  };

  const t = content[lang];

  return (
    <div className="bg-brand-off-white text-brand-dark">
      <Header
        lang={lang}
        setLang={setLang}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        t={t}
        handleNavClick={handleNavClick}
        currentUser={currentUser}
      />
      <main>
        <HomeSection t={t} handleNavClick={handleNavClick} />
        <OpenLetterSection t={t} />
        <SlidesSection lang={lang} />
        <AboutSection t={t} />
        <EventSection t={t} />
        <OurWorkSection t={t} />
        <DonateSection t={t} />
        <QuestionsSection t={t} />
        <ContactSection t={t} />
        <FeedbackSection
          open={isFeedbackOpen}
          setOpen={setIsFeedbackOpen}
          t={t}
        />
        {/* Floating Feedback Button */}
        <button
          onClick={() => setIsFeedbackOpen(true)}
          className="fixed bottom-6 right-6 rounded-full p-4 shadow-lg bg-[#D9A443] hover:bg-[#E8BB68] text-white border"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      </main>
      <Footer t={t} currentYear={currentYear} />
    </div>
  );
}
